import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
  Button,
} from '@mui/material';
import { ThemePalette } from 'mui.theme';
import { keyframes } from '@emotion/react';
import { useState, useEffect } from 'react';
import type { Asset } from 'types/asset.types';
import { Chart } from './Chart';
import NoData from './Components/Fallback/NoData';
import { VehicleSelector } from './Components/VehicleSelector';
import {
  extractTireNotificationsForChart,
  extractTireLeakageNotificationsForChart,
} from 'views/TirePressure/util';
import { NotificationsTable } from './Components/VehicleInsightNotificationsTable';
import { composeUniqueKey } from 'utils/helpers/string.manipulation';
import type { MakeModelDetails } from 'types/make-model.types';
import type {
  TirePattern,
  TirePatternPosition,
  TpmsNotification,
  TpmsLeakageNotification,
} from 'types/tire-pattern.types';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  enableDarkTheme: boolean;
  comparingAsset: string;
  assets: Asset[];
  tireData: any;
  colors: string[];
  unitSystem: string;
  metricType: string;
  setMetricType: any;
  tireCodes: any;
  limits: any;
  startDate: Date;
  endDate: Date;
  selectedValue: string;
  makeModelDetails: MakeModelDetails[];
  tirePatterns: {
    tirePatterns: TirePattern[];
    tirePatternPositions: TirePatternPosition[];
  };
  customerId: string;
  batteryData: Record<
    string,
    Record<
      string,
      { isBatteryLow: boolean; voltageValue?: number; timeStamp?: number }
    >
  >;
  selectedRange: string;
}

const CompareContent = ({
  enableDarkTheme,
  comparingAsset,
  assets,
  tireData,
  colors,
  unitSystem,
  metricType,
  setMetricType,
  tireCodes,
  limits,
  startDate,
  endDate,
  selectedValue,
  makeModelDetails,
  tirePatterns,
  customerId,
  batteryData,
  selectedRange,
}: Props) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const [trigger, setTrigger] = useState(0);
  const [, setCompleted] = useState(0);
  const assetData = assets.find((item) => item.bumperNumber === comparingAsset);

  let assetTireData = {
    pressureData: {},
    notifications: [],
    leakageNotifications: [],
  };
  if (assetData) {
    assetTireData = tireData[assetData?.device.deviceId] ?? {
      pressureData: {},
      notifications: [],
      leakageNotifications: [],
    };
  }
  const [selectedTireCode, setSelectedTireCode] = useState<string | null>(null);
  const availableProfiles = Object.keys(assetTireData.pressureData || {});
  const [profiles, setProfiles] = useState<string[]>(availableProfiles);

  useEffect(() => {
    if (selectedTireCode && availableProfiles.includes(selectedTireCode)) {
      setProfiles([selectedTireCode]);
    } else {
      setProfiles(availableProfiles);
    }
  }, [selectedTireCode, availableProfiles]);

  const hasData = (tagData: any) => {
    if (!tagData.pressureData) {
      return false;
    }
    return Object.values(tagData.pressureData).some(
      (array: any) => array.length > 0
    );
  };

  const textColor = enableDarkTheme
    ? ThemePalette.typography.wheat
    : ThemePalette.typography.black;

  const handleCallChildFunction = () => {
    setTrigger((prev) => prev + 1);
  };

  const handleChildComplete = () => {
    setCompleted((prev) => prev + 1);
  };

  const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

  if (!assetData) {
    return <></>;
  }

  const isDataPresent = hasData(assetTireData);

  if (hasData(assetTireData)) {
    const filteredNotifications =
      assetTireData?.notifications?.filter((item: TpmsNotification) => {
        const hasTemperature = item?.rule?.ruleName
          ?.toLowerCase()
          .includes('temperature');
        const matchesMetricType =
          metricType === 'Temperature' ? hasTemperature : !hasTemperature;

        // Filter by selected tire code if one is selected
        if (selectedTireCode) {
          const tagName =
            item?.rule?.ruleConditionAssociations?.[0]?.tagName ?? '';
          const parts = tagName.split('_');
          let notificationTireCode = parts[2] || null;

          // Normalize tire code format
          if (
            notificationTireCode &&
            /^0x[0-9a-f]+$/i.test(notificationTireCode)
          ) {
            notificationTireCode =
              '0x' + notificationTireCode.slice(2).toUpperCase();
          }

          // Normalize selectedTireCode as well
          let normalizedSelectedTireCode = selectedTireCode;
          if (/^0x[0-9a-f]+$/i.test(selectedTireCode)) {
            normalizedSelectedTireCode =
              '0x' + selectedTireCode.slice(2).toUpperCase();
          }

          return (
            matchesMetricType &&
            notificationTireCode === normalizedSelectedTireCode
          );
        }

        return matchesMetricType;
      }) ?? [];

    const filteredLeakageNotifications =
      assetTireData?.leakageNotifications.filter(
        (item: TpmsLeakageNotification) => {
          const notificationTireCode = item.tireCode;
          return profiles.some((profile) => {
            return notificationTireCode === profile.toLowerCase();
          });
        }
      );

    let notificationData = extractTireNotificationsForChart(
      filteredNotifications
    );

    const leakageData = extractTireLeakageNotificationsForChart(
      filteredLeakageNotifications || []
    );

    notificationData = [...notificationData, ...leakageData];

    return (
      <Box>
        <Grid container spacing={2}>
          <Grid item lg={5} sm={12} md={6} xs={12}>
            <Box
              display={'flex'}
              flexDirection={'column'}
              border={'1px solid #2F445E'}
              borderRadius={'4px'}
            >
              <Box>
                <Box
                  sx={{
                    marginTop: '10px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0 8px',
                  }}
                >
                  <Typography
                    sx={{
                      color: textColor,
                      fontSize: '14px',
                      fontWeight: '700',
                    }}
                  >
                    {`Tire ${metricType} Trend for ${
                      assetData?.bumperNumber ?? ''
                    }`}
                  </Typography>
                  <Button
                    sx={{
                      backgroundColor: ThemePalette.typography.primary,
                      color: ThemePalette.typography.white,
                      textTransform: 'none',
                      height: '40px',
                      paddingLeft: '15px',
                      paddingRight: '15px',
                      fontSize: '12px',
                      border: `1px solid white`,
                      '&:hover': {
                        color: enableDarkTheme ? 'white' : 'black',
                        border: enableDarkTheme
                          ? '1px solid white'
                          : '1px solid black',
                      },
                    }}
                    onClick={handleCallChildFunction}
                  >
                    {t('export_to_csv')}
                  </Button>
                </Box>
                <Box
                  sx={{
                    padding: '10px',
                  }}
                >
                  <Chart
                    enableDarkTheme={enableDarkTheme}
                    data={assetTireData}
                    colors={colors}
                    allowedProfiles={profiles}
                    unitSystem={unitSystem}
                    tireCodes={tireCodes}
                    metricType={metricType}
                    setMetricType={setMetricType}
                    notifications={notificationData}
                    startDate={startDate}
                    endDate={endDate}
                    selectedAsset={assetData}
                    trigger={trigger}
                    onComplete={handleChildComplete}
                    chartKey={selectedValue}
                    makeModelDetails={makeModelDetails}
                    tirePatterns={tirePatterns}
                    customerId={customerId}
                    selectedRange={selectedRange}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  textAlign: 'center',
                }}
              >
                {Object.keys(assetTireData?.pressureData).map((item, index) => {
                  const values = Object.values(assetTireData?.pressureData);
                  // @ts-expect-error ignoring for checks
                  const hasNoData = values[index].length === 0;
                  return (
                    <FormControlLabel
                      key={index}
                      label={
                        <span
                          style={{
                            fontSize: '12px',
                            color: hasNoData
                              ? ThemePalette.typography.lightGrey
                              : colors[index],
                          }}
                        >
                          {tireCodes[
                            '0x' +
                              item.toLowerCase().replace('0x', '').toUpperCase()
                          ] ?? item}
                        </span>
                      }
                      disabled={hasNoData}
                      control={
                        <Checkbox
                          size="small"
                          sx={{
                            color: hasNoData
                              ? ThemePalette.typography.lightGrey
                              : colors[index],
                            '&.Mui-checked': {
                              color: hasNoData
                                ? ThemePalette.typography.lightGrey
                                : colors[index],
                            },
                            '&.Mui-disabled': {
                              color: ThemePalette.typography.lightGrey,
                            },
                            '&.Mui-disabled.Mui-checked': {
                              color: ThemePalette.typography.lightGrey,
                            },
                            '& svg': {
                              fontSize: '16px',
                            },
                          }}
                          checked={hasNoData ? false : profiles.includes(item)}
                          disabled={hasNoData}
                          onChange={() => {
                            if (hasNoData) return;

                            if (profiles.includes(item)) {
                              setProfiles(profiles.filter((e) => e !== item));
                            } else {
                              setProfiles([...profiles, item]);
                            }
                          }}
                        />
                      }
                      sx={{
                        color: hasNoData
                          ? ThemePalette.typography.lightGrey
                          : colors[index],
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          </Grid>
          <Grid item lg={7} sm={12} md={6} xs={12}>
            <Box
              sx={{
                border: '1px solid #2E435E',
                padding: '10px',
                borderRadius: '4px',
              }}
            >
              <Grid container spacing={2}>
                {isDataPresent && (
                  <Box
                    sx={{
                      marginTop: '15px',
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <Button
                      sx={{
                        backgroundColor: '#133063',
                        color: 'white',
                        textTransform: 'none',
                        borderRadius: '999px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        '&:hover': {
                          backgroundColor: '#133063',
                        },
                        paddingLeft: '20px',
                        paddingRight: '20px',
                        width: '100px',
                        cursor: 'none',
                      }}
                      disableRipple
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: 'red',
                          animation: `${blink} 1s infinite`,
                        }}
                      />
                      <Typography
                        fontWeight="bold"
                        sx={{
                          fontSize: '14px',
                        }}
                      >
                        {t('dm_log_live')}
                      </Typography>
                    </Button>
                  </Box>
                )}
                <VehicleSelector
                  asset={assetData}
                  tireData={assetTireData}
                  limits={limits}
                  assetType={assetData?.assetType ?? ''}
                  enableDarkTheme={enableDarkTheme}
                  unitSystem={unitSystem}
                  tireCodes={tireCodes}
                  metricType={metricType}
                  makeModel={
                    assetData?.make && assetData?.model
                      ? composeUniqueKey([assetData.make, assetData.model], '-')
                      : ''
                  }
                  customerId={customerId}
                  makeModelDetails={makeModelDetails}
                  tirePatterns={tirePatterns}
                  batteryData={batteryData}
                  selectedAsset={assetData}
                  onTireClick={(tireCode) => {
                    const newSelected =
                      selectedTireCode === tireCode ? null : tireCode;
                    setSelectedTireCode(newSelected);
                  }}
                />
              </Grid>
            </Box>
          </Grid>
        </Grid>
        <Box
          sx={{
            border: '1px solid #2F445E',
            marginTop: '10px',
            borderRadius: '4px',
          }}
        >
          <Typography
            sx={{
              fontSize: '14px',
              color: palette.textPrimary,
              fontWeight: '700',
              padding: '10px',
              marginTop: '10px',
            }}
          >
            {t('notification_history')}
          </Typography>

          {(assetTireData?.notifications?.length === 0 &&
            assetTireData?.leakageNotifications?.length === 0) ||
          (filteredNotifications?.length === 0 &&
            filteredLeakageNotifications?.length === 0) ? (
            <Typography
              sx={{
                fontSize: '12px',
                color: palette.textPrimary,
                paddingLeft: '10px',
                marginBottom: '10px',
              }}
            >
              {t('no_notifications_available_2')}
            </Typography>
          ) : (
            <NotificationsTable
              enableDarkTheme={enableDarkTheme}
              notifications={filteredNotifications}
              customerId={customerId}
              makeModelDetails={makeModelDetails}
              tirePatterns={tirePatterns}
              leakageNotifications={filteredLeakageNotifications}
              tireCodes={tireCodes}
            />
          )}
        </Box>
      </Box>
    );
  } else {
    return (
      <NoData
        height={'300px'}
        text={`No data available for asset ${assetData?.bumperNumber}.`}
        textFontSize={'16px'}
      />
    );
  }
};

export default CompareContent;
